/**
 * PublicationSeriesService
 *
 * Source of truth: docs/03-technical/SPEC-PUBLICATION-SERIES-SCHEMA.md § 11
 * Issue: #167 (Sprint X Phase 1A)
 *
 * Owns CRUD + niche/profile validation for PublicationSeries. Does NOT
 * read or write IssueSection / EditorialBrief / FreshnessEvent /
 * SourcePreference — those are owned by services that ship in Sprint 8+.
 */

import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { createValidationError } from '../utils/StandardErrorFormat.js';

const VALID_CADENCES = ['weekly', 'biweekly', 'monthly', 'quarterly', 'adhoc'];

const VALID_NICHES = [
  'retirees',
  'hnw',
  'tech_employees',
  'medical_professionals',
  'busy_parents',
];

// Per spec § 6 — niche → audienceProfile seed map
const NICHE_PROFILE_SEED = {
  retirees: {
    lifeStage: 'retired',
    primaryConcern: 'income',
    technicalDepth: 'basic',
    planningFocus: ['rmd', 'ltc', 'estate_planning', 'tax_efficient_withdrawal'],
  },
  hnw: {
    lifeStage: 'pre_retirement',
    primaryConcern: 'tax_minimization',
    technicalDepth: 'intermediate',
    planningFocus: ['estate_planning', 'tax_loss_harvesting', 'charitable_giving'],
  },
  tech_employees: {
    lifeStage: 'accumulating',
    primaryConcern: 'liquidity_event',
    technicalDepth: 'intermediate',
    planningFocus: ['rsu_planning', 'iso_planning', 'concentrated_stock', 'ipo_prep'],
  },
  medical_professionals: {
    lifeStage: 'accumulating',
    primaryConcern: 'tax_minimization',
    technicalDepth: 'basic',
    planningFocus: ['student_loan_strategy', 'disability_insurance', 'self_employment_tax'],
  },
  busy_parents: {
    lifeStage: 'accumulating',
    primaryConcern: 'wealth_growth',
    technicalDepth: 'basic',
    planningFocus: ['college_savings', 'life_insurance', 'first_home', '529_strategy'],
  },
};

const arraysEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

const fieldsDiffer = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) return !arraysEqual(a, b);
  return a !== b;
};

class PublicationSeriesService {
  constructor(client = null) {
    this.logger = logger.child('PublicationSeriesService');
    this.client = client || generateClient();
  }

  async createSeries(input = {}) {
    if (!input.name || input.name === '') {
      createValidationError('name is required', { code: 'NAME_REQUIRED' });
    }
    if (!input.cadence || !VALID_CADENCES.includes(input.cadence)) {
      createValidationError(
        `cadence must be one of ${VALID_CADENCES.join(', ')}`,
        { code: 'INVALID_CADENCE' }
      );
    }

    const payload = {
      lastIssueNumber: 0,
      status: 'active',
      showSourceAttribution: true,
      requiresApprovalBeforeSend: false,
      audienceProfileOverridden: false,
      ...input,
    };

    const { data } = await this.client.models.PublicationSeries.create(payload);
    return data;
  }

  async getSeries(seriesId) {
    const { data } = await this.client.models.PublicationSeries.get({ id: seriesId });
    return data;
  }

  async listSeriesByUser(userId) {
    const { data } = await this.client.models.PublicationSeries.list({
      filter: { userId: { eq: userId } },
    });
    return data || [];
  }

  async updateSeries(seriesId, updates) {
    const { data } = await this.client.models.PublicationSeries.update({
      id: seriesId,
      ...updates,
    });
    return data;
  }

  async archiveSeries(seriesId) {
    return this.updateSeries(seriesId, { status: 'archived' });
  }

  async deleteSeries(seriesId) {
    await this.client.models.PublicationSeries.delete({ id: seriesId });
  }

  async setNiche(seriesId, niche) {
    if (!VALID_NICHES.includes(niche)) {
      createValidationError(
        `niche must be one of ${VALID_NICHES.join(', ')}`,
        { code: 'INVALID_NICHE' }
      );
    }

    const series = await this.getSeries(seriesId);
    const seed = NICHE_PROFILE_SEED[niche];

    // If overridden, never touch the profile — just update niche.
    if (series.audienceProfileOverridden === true) {
      return this.updateSeries(seriesId, { niche });
    }

    const existing = series.audienceProfile;
    const profileEmpty =
      !existing ||
      (existing.lifeStage == null &&
        existing.primaryConcern == null &&
        existing.technicalDepth == null &&
        (existing.planningFocus == null || existing.planningFocus.length === 0));

    if (profileEmpty) {
      return this.updateSeries(seriesId, { niche, audienceProfile: seed });
    }

    // Profile has values. Compare against new niche's seed.
    const conflicts = [];
    for (const key of ['lifeStage', 'primaryConcern', 'technicalDepth']) {
      if (fieldsDiffer(existing[key], seed[key])) {
        conflicts.push({ field: key, current: existing[key], seedFor: seed[key] });
      }
    }
    if (!arraysEqual(existing.planningFocus, seed.planningFocus)) {
      conflicts.push({
        field: 'planningFocus',
        current: existing.planningFocus,
        seedFor: seed.planningFocus,
      });
    }

    if (conflicts.length > 0) {
      createValidationError(
        `Niche '${niche}' contradicts existing audienceProfile (NICHE_PROFILE_CONTRADICTION)`,
        { code: 'NICHE_PROFILE_CONTRADICTION', conflicts }
      );
    }

    // Profile already matches the seed — idempotent no-op on profile, just set niche.
    return this.updateSeries(seriesId, { niche });
  }

  async updateAudienceProfile(seriesId, partialProfile) {
    const series = await this.getSeries(seriesId);
    const current = series.audienceProfile || {};

    // Detect a real change so we don't flip the override flag for no reason.
    let changed = false;
    for (const key of Object.keys(partialProfile)) {
      if (fieldsDiffer(current[key], partialProfile[key])) {
        changed = true;
        break;
      }
    }

    const merged = { ...current, ...partialProfile };
    const updates = { audienceProfile: merged };
    if (changed) updates.audienceProfileOverridden = true;

    return this.updateSeries(seriesId, updates);
  }

  async claimSignatureSections(seriesId, sectionKeys) {
    const { data: catalog } = await this.client.models.RecurringSection.list();
    const validKeys = new Set((catalog || []).map((s) => s.sectionKey));

    for (const key of sectionKeys) {
      if (!validKeys.has(key)) {
        createValidationError(
          `Unknown sectionKey '${key}' (UNKNOWN_SECTION_KEY)`,
          { code: 'UNKNOWN_SECTION_KEY', sectionKey: key }
        );
      }
    }

    return this.updateSeries(seriesId, { signatureSectionKeys: sectionKeys });
  }

  // Atomic counter — a stale read can lose updates under concurrency, so we
  // serialize per-seriesId via an in-memory promise chain. Conditional updates
  // at the resolver layer ship in Phase 1B; this guards the singleton path now.
  _incrementChain = new Map();

  async incrementIssueNumber(seriesId) {
    const prior = this._incrementChain.get(seriesId) || Promise.resolve();
    const next = prior.then(async () => {
      const series = await this.getSeries(seriesId);
      const newValue = (series.lastIssueNumber || 0) + 1;
      const { data } = await this.client.models.PublicationSeries.update({
        id: seriesId,
        lastIssueNumber: newValue,
      });
      return data?.lastIssueNumber ?? newValue;
    });
    this._incrementChain.set(seriesId, next.catch(() => {}));
    return next;
  }

  /**
   * Phase 1B (#168) — atomically wrap setNiche + updateAudienceProfile so the
   * onboarding wizard can submit niche + customised profile in a single call.
   *
   * @param {Object} args
   * @param {string} args.seriesId
   * @param {string} args.niche — one of VALID_NICHES
   * @param {Object} [args.profile] — partial audienceProfile; only applied when overridden=true
   * @param {boolean} [args.overridden=false] — true after the user edits any profile field
   */
  async setNicheWithProfile({ seriesId, niche, profile, overridden = false }) {
    const afterNiche = await this.setNiche(seriesId, niche);
    if (!overridden || !profile || Object.keys(profile).length === 0) {
      return afterNiche;
    }
    return this.updateAudienceProfile(seriesId, profile);
  }

  /**
   * Phase 1B (#168) — list RecurringSection rows whose recommendedNiches include
   * the given niche. Used by SignatureSectionPicker + NicheStep preview.
   *
   * @param {Object} args
   * @param {string} args.niche — one of VALID_NICHES
   * @returns {Promise<Array<RecurringSection>>}
   */
  async getRecommendedSections({ niche } = {}) {
    if (!niche || !VALID_NICHES.includes(niche)) {
      createValidationError(
        `niche must be one of ${VALID_NICHES.join(', ')}`,
        { code: 'INVALID_NICHE' }
      );
    }
    const { data } = await this.client.models.RecurringSection.list();
    return (data || []).filter(
      (row) => Array.isArray(row.recommendedNiches) && row.recommendedNiches.includes(niche)
    );
  }

  async getOrCreatePlaceholderSeries(userId) {
    const existing = await this.listSeriesByUser(userId);
    if (existing.length > 0) {
      // Use oldest as the canonical link target — re-runs of the backfill must
      // not create a second placeholder for the same user.
      const sorted = [...existing].sort((a, b) =>
        (a.createdAt || '').localeCompare(b.createdAt || '')
      );
      return sorted[0];
    }
    return this.createSeries({
      userId,
      name: '__needs_naming__',
      status: 'pending_setup',
      cadence: 'weekly', // per spec Q1 — fallback when newsletterDefaults absent
    });
  }
}

// Phase 1B: re-exported for the onboarding NicheStep (avoids a duplicate
// seed map drifting out of sync — review feedback PR #221).
export { PublicationSeriesService, VALID_NICHES, NICHE_PROFILE_SEED };
export default new PublicationSeriesService();
